import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, EmptyState } from "../components/EmptyState";
import { api, type ConnectionsResponse, type ProviderStatus } from "../lib/api";

const DEFAULT_PROVIDERS: ProviderStatus[] = [
  { id: "garmin", label: "Garmin", connected: false, last_sync_at: null, connect_url: "/auth/garmin/start" },
  { id: "samsung", label: "Galaxy Watch", connected: false, last_sync_at: null, is_bridge: true },
  { id: "withings", label: "Withings scale", connected: false, last_sync_at: null, connect_url: "/auth/withings/start" },
];

function ProviderCard({ p }: { p: ProviderStatus }) {
  return (
    <div className="card card-pad flex items-center justify-between gap-4">
      <div>
        <div className="font-medium">{p.label}</div>
        {p.connected ? (
          <div className="mt-1 flex items-center gap-2">
            <span className="pill-positive">Connected</span>
            <span className="muted">last sync {p.last_sync_at}</span>
          </div>
        ) : (
          <div className="muted mt-1">Not connected</div>
        )}
      </div>
      <div className="shrink-0">
        {p.connect_url ? (
          <a href={p.connect_url} className="btn-accent">
            Connect
          </a>
        ) : p.is_bridge ? (
          <div className="flex items-center gap-2 text-right">
            <span className="pill-neutral">Sideloaded app</span>
            <span className="muted">Install the Galaxy bridge app on your phone</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function Connections() {
  const [data, setData] = useState<ConnectionsResponse | null | "error">(null);

  useEffect(() => {
    api.connections().then(setData).catch(() => setData("error"));
  }, []);

  const providers = data === "error" ? DEFAULT_PROVIDERS : data?.providers ?? null;

  return (
    <div className="mx-auto max-w-2xl animate-fade-in">
      <PageHeader title="Connections" eyebrow="Wearables & scale" />

      {providers === null ? (
        <EmptyState title="Loading connections" />
      ) : providers.length === 0 ? (
        <EmptyState title="No providers yet">
          Connections will appear here once the backend is wired up.
        </EmptyState>
      ) : (
        <>
          <div className="space-y-3">
            {providers.map((p) => (
              <ProviderCard key={p.id} p={p} />
            ))}
          </div>
          <p className="muted mt-4">
            Garmin uses a cloud connection; the Galaxy Watch syncs via a companion app on your phone.{" "}
            <Link to="/help" className="text-accent hover:underline font-medium">
              Step-by-step guide →
            </Link>
          </p>
        </>
      )}
    </div>
  );
}
